import Konva from 'konva';
import HaspObject from './HaspObject';
import PageOverlay from '../draw/PageOverlay';
import { getActiveTheme } from './properties/ConfigManager.js';
import { getObjectSchema } from './properties/v2/wireFormat.js';
import { computeEffectiveStyleEntries, applyEffectiveStyleEntry } from './properties/v2/exportOptimizer.js';
import { allocateDesignerObjectId } from '../flow/designerObjectIds.js';
import { exportPageLogic } from '../logic/pageLogic.js';
import { exportPageHass } from '../hass/pageHass.js';

/**
 * Extract entity ID strings from a serialised automation[] JSON array.
 * Reads raw JSON — does not import AutomationModel to avoid circular deps.
 * @private
 */
function _extractEntitiesFromAutomation(automationArr) {
  return (automationArr ?? []).flatMap(modelJson => {
    const actionEntities = (modelJson.actions ?? []).flatMap(action => {
      const eidParam = (action.parameters ?? []).find(p => p.key === 'entity_id');
      if (!eidParam) return [];
      const val = eidParam.value;
      if (val?.kind === 'list')      return (val.items ?? []).filter(Boolean);
      if (val?.kind === 'literal')   return val.value   ? [val.value]   : [];
      if (val?.kind === 'template')  return val.template ? [val.template] : [];
      if (val?.kind === 'reference') return val.name    ? [val.name]    : [];
      return [];
    });
    // State-binding entities count too — {{ tag }} is only safe when the
    // widget's ENTIRE config (bindings and actions alike) points at one
    // single entity, not just its actions.
    // LEGACY COMPATIBILITY — reads raw serialized JSON, which may be the
    // current shape (strategy: 'state') or an older save from before the
    // strategy rename (source: 'entity') — see AutomationBinding.js. Drop
    // the `b.source === 'entity'` half once no supported project predates
    // that rename.
    const bindingEntities = (modelJson.bindings ?? [])
      .filter(b => (b.strategy === 'state' || b.source === 'entity') && b.entityId)
      .map(b => b.entityId);
    return [...actionEntities, ...bindingEntities];
  }).filter(Boolean);
}

/**
 * Page number 0 refers to an object visible on all pages.
 *
 * An id of 0 refers to the page itself instead of an object.
 *
 * A page can have the following attributes:
 *   prev : The number of the destination page when performing a page prev action on this page
 *   next : The number of the destination page when performing a page next action on this page
 *   back : The number of the destination page when performing a page back action on this page
 */

export default class HaspPage extends HaspObject {
  page_limit_command = undefined;
  page_limit_number = undefined;
  page_limit_commands = [];
  top_haspid = 1;
  displayShape = 'rectangular';
  // Event/action node graph (src/logic) — design data, compiled at export.
  logic = null;
  _pendingLogic = null;
  // Home Assistant flow graph (src/hass) — design data, compiled at export.
  hass = null;
  _pendingHass = null;

  pageZeroOverlay = undefined;

  constructor(config) {
    config.comment ??= 'Page ' + config.haspid;
    super(config);
    this.setDisplayShape(config.displayShape ?? 'rectangular');
    this.draggable(false);
  }

  setDisplayShape(shape) {
    const nextShape = shape === 'round' ? 'round' : 'rectangular';
    this.displayShape = nextShape;
    const pageRadius = nextShape === 'round'
      ? Math.min(this.width(), this.height()) / 2
      : (this.styleProp?.('main', 'radius') ?? 0);
    this.rect?.cornerRadius?.(pageRadius);

    this.clipFunc(undefined);
    this.getLayer?.()?.batchDraw?.();
  }

  toDataURL(config = {}) {
    return super.toDataURL({
      x: 0,
      y: 0,
      width: this.width(),
      height: this.height(),
      ...config,
    });
  }

  setActive(active) {
    if (active) {
      this.moveToTop();
    }
  }

  getUsedIds() {
    const ids = new Set();
    const collect = (node) => {
      if (node instanceof HaspObject && node !== this && node.haspid != null) {
        ids.add(node.haspid);
      }
      node.getChildren?.().forEach(collect);
    };
    this.getChildren().forEach(collect);
    return ids;
  }

  getNextId(options = {}) {
    const id = allocateDesignerObjectId(this, this.getLayer?.(), this.top_haspid, options);
    options.reservedIds?.add(id);
    this.top_haspid = id + 1;
    return id;
  }

  setNextId(nextid) {
    if (nextid > this.top_haspid) {
      this.top_haspid = nextid;
    }
  }

  addPageNav(command, pagenumber) {
    this.page_limit_commands.push({ page_limit_command: command, page_limit_number: pagenumber })
  }

  deletePageNav(index) {
    this.page_limit_commands.splice(index, 1);
  }

  setPageZeroOverlay(overlay) {
    if (overlay !== undefined) {
      if (this.pageZeroOverlay === undefined) {
        this.pageZeroOverlay = new PageOverlay(this, overlay);
        this.add(this.pageZeroOverlay)
      } else {
        this.pageZeroOverlay.image(overlay);
      }
      this.pageZeroOverlay.x(0);
      this.pageZeroOverlay.y(0);
      this.pageZeroOverlay.moveToTop();
    } else {
      if (this.pageZeroOverlay !== undefined) {
        this.pageZeroOverlay.destroy();
        this.pageZeroOverlay = undefined;
      }
    }
  }

  drawHit(can, top) {
    const savedClipFunc = this.clipFunc();
    this.clipFunc(undefined);
    const result = super.drawHit(can, top);
    this.clipFunc(savedClipFunc);
    return result;
  }

  getPageImage() {
    return this.toDataURL({ pixelRatio: 2 });
  }

  export(page, exportData, includeHass, isDesignSave = false) {
    let pageData = { page: this.haspid, comment: this.comment }

    const obj_theme = getActiveTheme()[this.obj] ?? {};

    if (this.swipe) pageData.swipe = 1;
    if (this.transition !== undefined) pageData.transition = this.transition;
    if (this.transition_direction !== undefined) pageData.transition_direction = this.transition_direction;
    if (this.transition_duration !== undefined) pageData.transition_duration = this.transition_duration;

    // Part-scoped style properties, every semantic state included — see the
    // matching block/comment in ObjectExport.js. computeEffectiveStyleEntries
    // is the single, schema-agnostic optimizer used by both object and page
    // export; it performs zero cascade/default comparison itself.
    const _pageSchema = getObjectSchema(this.obj) ?? { parts: {}, properties: {} };
    for (const entry of computeEffectiveStyleEntries(this, _pageSchema)) {
      applyEffectiveStyleEntry(this, pageData, obj_theme, entry);
    }

    if (isDesignSave) {
      const logic = exportPageLogic(this);
      if (logic) pageData.logic = logic;
      const hass = exportPageHass(this);
      if (hass) pageData.hass = hass;
    }

    exportData.push(pageData);

    if (this.page_limit_commands.length > 0) {
      var pageNav = { page: this.haspid, id: 0 }
      this.page_limit_commands.forEach(nav => {
        pageNav[nav.page_limit_command] = nav.page_limit_number;
      });
      exportData.push(pageNav);
    }

    this.getChildren().forEach(element => {
      if (element instanceof HaspObject) {
        element.export(page, exportData, includeHass, isDesignSave);
      }
    });
  }

  /**
   * Objects with Home Assistant configuration, as generateObjectYaml options.
   * `configOf(node)` reads a node's configuration; the Exporter passes one
   * that includes the page's flow graph (hass/hassEffective.js).
   */
  getHassYamlEntries(page, { configOf = node => node.hass_config } = {}) {
    const entries = [];
    const visited = new Set();

    const visit = (element) => {
      if (!(element instanceof HaspObject) || visited.has(element)) return;
      visited.add(element);

      if (element.obj === 'chart') {
        console.log('[YAML chart]', element.haspid, JSON.stringify(element.hass_config));
      }
      const cfg = configOf(element);
      if (cfg &&
          ((cfg.properties  || []).length > 0 ||
           (cfg.events       || []).length > 0 ||
           (cfg.automation   || []).length > 0 ||
           (cfg.chart_series || []).some(s => s.entity))) {
        // Compute the entity set for useTag (one entity → use {{ tag }}).
        // Read from whichever format is active: new automation[] or legacy events[].
        let allEntities;
        if ((cfg.automation || []).length > 0) {
          allEntities = [
            ..._extractEntitiesFromAutomation(cfg.automation),
            ...(cfg.properties || []).map(p => p.entityId).filter(Boolean),
          ];
        } else {
          allEntities = [
            ...(cfg.events || []).flatMap(e => (e.entities || [e.entityId]).filter(Boolean)),
            ...(cfg.properties || []).map(p => p.entityId).filter(Boolean),
          ];
        }
        const uniqueEntities = [...new Set(allEntities)];
        const useTag = uniqueEntities.length === 1;
        entries.push({
          options: { page_id: page.haspid, hasp_id: element.haspid, object: element, hass_config: cfg, useTag, configOf },
        });
      }

      // Konva's getChildren() is the Designer's object-tree API. Walk in
      // depth-first order and inspect each HaspObject before its descendants,
      // preserving the existing page/object order while covering every depth.
      element.getChildren?.().forEach(visit);
    };

    this.getChildren().forEach(visit);

    return entries;
  }
}

// ---------------------------------------------------------------------------
// HaspTemplatePage — design canvas for transient popup objects
// ---------------------------------------------------------------------------
// Template pages are NOT exported in the plate JSONL.  Their objects are
// injected at runtime via openhasp.command. targetPage is the regular
// openHASP page they overlay at runtime. The read-only Designer canvas context
// is transient and is supplied by HaspObjectLayer.templatePreviewState.

export class HaspTemplatePage extends HaspPage {
  top_haspid = 201; // objects start at 201; 200 is reserved for auto-generated backdrop

  constructor(config) {
    const targetPage   = config.targetPage   ?? null;
    const underlayPage = config.underlayPage ?? null;
    config.comment ??= 'Template → P' + (targetPage ?? '?');
    super(config);
    this.targetPage   = targetPage;
    this.underlayPage = underlayPage;
    // Transparent background with a gray boundary marker — not configurable, not exported
    if (this.rect) {
      this.rect.fill('transparent');
      this.rect.opacity(1);
      this.rect.stroke('#606060');
      if (this.rect.setStrokeWidth) this.rect.setStrokeWidth(1);
      else this.rect.strokeWidth(1);
    }
  }

  export(page, exportData, includeHass, isDesignSave = false) {
    exportData.push({
      page:         this.haspid,
      comment:      this.comment,
      obj:          'template_page',
      targetPage:   this.targetPage,
      underlayPage: this.underlayPage ?? undefined,
      logic:        isDesignSave ? exportPageLogic(this) : undefined,
      hass:         isDesignSave ? exportPageHass(this) : undefined,
    });
    this.getChildren().forEach(element => {
      if (element instanceof HaspObject) {
        element.export(this, exportData, includeHass, isDesignSave);
      }
    });
  }

  getHassYamlEntries(_page, { configOf = node => node.hass_config } = {}) {
    const entries = [];
    const fallback = this.targetPage ?? _findTargetPageForTemplate(this);
    _collectTemplateEntries(this, entries, fallback, configOf);
    return entries;
  }
}

// ---------------------------------------------------------------------------
// Helpers for template page YAML generation
// ---------------------------------------------------------------------------

function _findTargetPageForTemplate(templatePage) {
  const layer = templatePage.getLayer?.();
  if (!layer) return null;

  const walk = (node) => {
    for (const child of (node.getChildren?.() ?? [])) {
      if (child instanceof HaspTemplatePage) continue;
      const cfg = child.hass_config ?? {};
      for (const ev of (cfg.events || [])) {
        if (ev.action === 'command' &&
            ev._popupTemplateId === templatePage.haspid &&
            ev._popupTargetPageId != null) {
          return ev._popupTargetPageId;
        }
      }
      if (typeof child.isContainer === 'function' && child.isContainer()) {
        const found = walk(child);
        if (found != null) return found;
      }
    }
    return null;
  };

  for (const page of (layer.getChildren?.() ?? [])) {
    if (page instanceof HaspTemplatePage) continue;
    if (!(page instanceof HaspPage)) continue;
    const found = walk(page);
    if (found != null) return found;
  }
  return null;
}

function _pageIdFromConfig(cfg) {
  for (const event of (cfg.events || [])) {
    if (event._deleteTargetPageId != null) return event._deleteTargetPageId;
    if (event._popupTargetPageId  != null) return event._popupTargetPageId;
  }
  return null;
}

function _collectTemplateEntries(node, entries, fallbackPageId, configOf) {
  (node.getChildren?.() ?? []).forEach(element => {
    if (!(element instanceof HaspObject)) return;
    const cfg = configOf(element) ?? {};
    if ((cfg.properties || []).length > 0 || (cfg.events || []).length > 0 || (cfg.automation || []).length > 0) {
      const page_id = fallbackPageId ?? _pageIdFromConfig(cfg);
      const hasp_id = Number(element.haspid);
      if (page_id != null && hasp_id != null) {
        entries.push({
          options: {
            page_id,
            hasp_id,
            object:      element,
            hass_config: cfg,
            useTag:      true,
            configOf,
          },
        });
      }
    }
    if (element.isContainer?.()) {
      _collectTemplateEntries(element, entries, fallbackPageId, configOf);
    }
  });
}
